import type { CoachOverlayPreview, LearnerOverlayPreview, OverlayAnnotationPreview } from '../utils/overlayPreview';
import { overlayInkRoleLabelZh, overlayKindLabelZh } from '../utils/overlayPreview';

function AnnotationList({ items }: { items: OverlayAnnotationPreview[] }) {
  if (!items.length) return null;
  return (
    <div className="compare-qa-pap-block">
      <h4>标注点</h4>
      <ol className="compare-qa-pap-steps">
        {items.map((ann, index) => {
          const kindZh = overlayKindLabelZh(ann.kind);
          const roleZh = overlayInkRoleLabelZh(ann.inkRole);
          const title = ann.label || kindZh || '标注';
          return (
            <li key={`${ann.id}-${index}`}>
              <div className="compare-qa-pap-step-head">
                <span className="compare-qa-pap-step-n is-muted">{index + 1}</span>
                <span className="compare-qa-pap-step-title">
                  {title}
                  {roleZh ? <em className="compare-qa-pap-new">{roleZh}</em> : null}
                </span>
              </div>
              {ann.id ? (
                <p className="compare-qa-pap-step-obj compare-qa-overlay-id" title={ann.id}>
                  编号：{ann.id}
                </p>
              ) : null}
              {kindZh && kindZh !== title ? (
                <p className="compare-qa-pap-step-obj">类型：{kindZh}</p>
              ) : null}
              {ann.text ? <p className="compare-qa-pap-step-reason">{ann.text}</p> : null}
              {ann.boxLabel ? (
                <p className="compare-qa-pap-step-obj compare-qa-overlay-box">{ann.boxLabel}</p>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function CoachOverlayEnrichBody({ preview }: { preview: CoachOverlayPreview }) {
  return (
    <div className="compare-qa-enrich-body">
      {!preview.hasContent ? (
        <div className="compare-qa-enrich-row">
          <span className="compare-qa-enrich-v compare-qa-enrich-reason">无教练层内容</span>
        </div>
      ) : (
        <div className="compare-qa-pap-rich">
          {preview.admonitions.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>嘱咐</h4>
              <ul className="compare-qa-enrich-tags">
                {preview.admonitions.map((item, index) => (
                  <li key={`adm-${index}`} title={item.annotationIds.join(', ') || undefined}>
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.traps.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>陷阱</h4>
              <ol className="compare-qa-pap-steps">
                {preview.traps.map((trap, index) => (
                  <li key={`trap-${index}`}>
                    <div className="compare-qa-pap-step-head">
                      <span className="compare-qa-pap-step-n">{index + 1}</span>
                      <span className="compare-qa-pap-step-title">{trap.title || '陷阱'}</span>
                    </div>
                    {trap.wrongHint ? (
                      <p className="compare-qa-pap-step-reason">易错：{trap.wrongHint}</p>
                    ) : null}
                    {trap.rightHint ? (
                      <p className="compare-qa-pap-step-obj">正解：{trap.rightHint}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {preview.strategyTags.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>策略标签</h4>
              <ul className="compare-qa-enrich-tags">
                {preview.strategyTags.map((tag, index) => (
                  <li key={`stg-${index}`}>{tag}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.identityBank.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>恒等式</h4>
              <ul className="compare-qa-enrich-tags">
                {preview.identityBank.map((item, index) => (
                  <li key={`id-${index}`}>{item.expr}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.methodVariants.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>手法变体</h4>
              <ol className="compare-qa-pap-steps">
                {preview.methodVariants.map((item, index) => (
                  <li key={`mv-${index}`}>
                    <div className="compare-qa-pap-step-head">
                      <span className="compare-qa-pap-step-n is-muted">{item.problemRef || index + 1}</span>
                      <span className="compare-qa-pap-step-title">
                        {item.methods[0] || '手法'}
                      </span>
                    </div>
                    {item.methods.slice(1).map((m, mi) => (
                      <p key={`m-${mi}`} className="compare-qa-pap-step-reason">{m}</p>
                    ))}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          <AnnotationList items={preview.annotations} />
        </div>
      )}
    </div>
  );
}

export function LearnerOverlayEnrichBody({ preview }: { preview: LearnerOverlayPreview }) {
  return (
    <div className="compare-qa-enrich-body">
      {!preview.hasContent ? (
        <div className="compare-qa-enrich-row">
          <span className="compare-qa-enrich-v compare-qa-enrich-reason">无学情层内容</span>
        </div>
      ) : (
        <div className="compare-qa-pap-rich">
          {preview.struggleSignals.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>挣扎信号</h4>
              <ol className="compare-qa-pap-steps">
                {preview.struggleSignals.map((item, index) => (
                  <li key={`sg-${index}`}>
                    <div className="compare-qa-pap-step-head">
                      <span className="compare-qa-pap-step-n">{index + 1}</span>
                      <span className="compare-qa-pap-step-title">
                        {overlayKindLabelZh(item.kind) || '信号'}
                      </span>
                    </div>
                    {item.summary ? (
                      <p className="compare-qa-pap-step-reason">{item.summary}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {preview.scratchPaths.length > 0 ? (
            <div className="compare-qa-pap-block">
              <h4>草稿路径</h4>
              <ol className="compare-qa-pap-steps">
                {preview.scratchPaths.map((item, index) => (
                  <li key={`sp-${index}`}>
                    <div className="compare-qa-pap-step-head">
                      <span className="compare-qa-pap-step-n is-muted">{index + 1}</span>
                      <span className="compare-qa-pap-step-title">{item.summary}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          <AnnotationList items={preview.annotations} />
        </div>
      )}
    </div>
  );
}
